import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  isBlockAnswered,
  type NumberRequest,
  type UpdateNumberRequestData,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import { EditableBlock } from "./editable-block";

/**
 * Where we write about this request: the tracking link of each parcel we ship,
 * and the day the number goes live.
 *
 * Optional — left empty, those e-mails go to the account's owners. It exists for
 * the person who ordered the line and is not the one who signs in, or who will
 * sign in later.
 */
export function NotificationEmail({
  request,
  isSaving,
  onSave,
  readOnly,
}: {
  request: NumberRequest;
  isSaving: boolean;
  onSave: (data: UpdateNumberRequestData) => Promise<boolean>;
  readOnly?: boolean;
}) {
  const { t } = useTranslation("numberRequests");
  const saved = request.notification_email || "";
  const [email, setEmail] = useState(saved);

  useEffect(() => setEmail(saved), [saved]);

  const submit = async (event: React.FormEvent, close: () => void) => {
    event.preventDefault();
    if (
      await onSave({
        notification_email: email.trim(),
        answered_block: "notification_email",
      })
    ) {
      close();
    }
  };

  return (
    <EditableBlock
      title={t("numberRequests.details.notificationEmail")}
      description={t("numberRequests.details.notificationEmailHelp")}
      filled={saved !== ""}
      answered={isBlockAnswered(request, "notification_email")}
      optional
      readOnly={readOnly}
      summary={saved ? <p className="text-sm">{saved}</p> : undefined}
    >
      {(close) => (
        <form onSubmit={(event) => submit(event, close)} className="space-y-3">
          <Input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t(
              "numberRequests.details.notificationEmailPlaceholder",
            )}
          />
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={isSaving}>
              {isSaving
                ? t("numberRequests.details.saving")
                : t("numberRequests.details.save")}
            </Button>
          </div>
        </form>
      )}
    </EditableBlock>
  );
}
